/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import ApiService from '../../../services/ApiService';
import {
    TextField, MenuItem, Button, Typography, Box, Paper,
    CircularProgress, Alert, Divider, FormHelperText, Autocomplete,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Checkbox, FormControlLabel, Chip,
    FormControl, InputLabel, Select,
    Accordion, AccordionSummary, AccordionDetails,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SITE_TYPE_OPTIONS = ['Normal Site', 'Call Center', 'Data Center'];
const DATA_USAGE_OPTIONS = ['Basic Data', 'Heavy Data', 'Voice', 'Video', 'Mixed'];
const YES_NO = ['Yes', 'No'];
const WAN_LINK_COMBOS = [
    { value: 'Tier 1/2 — 2x P2P / 2x DIA', circuits: 2 },
    { value: 'Tier 3 — 1 DIA + 1 Broadband', circuits: 2 },
    { value: 'Tier 4/5 — 1 Broadband (+ optional mobile)', circuits: 1 },
];

const PHONE_REGEX = /^\+[\d\s()./-]{7,20}$/;
const validatePhone = (value) => {
    if (!value) return 'Phone number is required';
    if (!PHONE_REGEX.test(value.trim())) return 'Format: +XX (XXX) XXX-XXXX';
    return null;
};
const validateEmail = (value) => {
    if (!value) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Enter a valid email address';
    return null;
};

const FIELD_SX = { '& .MuiFormHelperText-root': { minHeight: '1.25em' } };

// This project's MUI Grid (v7) doesn't behave like the legacy item/xs/sm API used
// elsewhere in older docs — every real form in this codebase (CircuitOrderForm.js)
// lays rows out with plain flex Boxes instead. Row/F mirror that convention.
const Row = ({ children, sx = {} }) => (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5, ...sx }}>{children}</Box>
);
const F = ({ children, grow = 1 }) => (
    <Box sx={{ flex: `${grow} 1 260px`, minWidth: 0 }}>{children}</Box>
);

// Tolerant TEM row-data lookup — TEM column names carry irregular internal spacing
// (e.g. "Port Bandwidth (in Mbps  )" with a double space before the closing paren).
const _findRowValue = (row, candidateKeys) => {
    if (!row) return undefined;
    const norm = (s) => String(s).replace(/\s+/g, ' ').trim().toLowerCase();
    const keys = Object.keys(row);
    for (const candidate of candidateKeys) {
        const match = keys.find((k) => norm(k) === norm(candidate));
        if (match && row[match] !== null && row[match] !== undefined && row[match] !== '') return row[match];
    }
    return undefined;
};

const SiteExpansionForm = ({
    watch,
    userRole,
    isUpdateMode,
    updateId,
    externalDemandData,
}) => {
    const navigate = useNavigate();
    const { accounts } = useMsal();
    const userName = accounts[0]?.name || '';

    const [localData, setLocalData] = useState({
        oe_name: '',
        debtor: '',
        cost_centre: '',
        site_id: '',
        country: '',
        city: '',
        site_address: '',
        primary_location_id: '',
        site_type: 'Normal Site',
        is_colocated: false,
        colocated_oes: '',
        desired_installation_date: '',

        wan_in_scope: true,
        wan_change_type: '', // UI only: 'Additional Link' | 'Bandwidth Upgrade'
        num_additional_links: '',
        lan_in_scope: true,
        wifi_in_scope: true,
        voice_in_scope: true,

        num_users: '',
        num_phones: '',
        num_vc_devices: '',
        existing_bandwidth_mbps: '',
        num_existing_wan_circuits: '',
        required_bandwidth_mbps: '',
        num_wan_circuits_required: '',
        link_type: '',
        wan_fe_required: false,
        circuit_already_ordered: false,
        circuit_demand_number: '',

        num_floors: '',
        num_static_devices: '',
        num_comm_rooms: '',
        num_users_per_floor: '',
        lan_fe_required: false,

        wifi_survey_done: '',
        wifi_survey_planned_date: '',
        heatmap_file_name: '', heatmap_file_base64: '', heatmap_file_size: null,
        survey_result_file_name: '', survey_result_file_base64: '', survey_result_file_size: null,
        floor_plan_file_name: '', floor_plan_file_base64: '', floor_plan_file_size: null,
        survey_file_name: '', survey_file_base64: '', survey_file_size: null, // general survey attachment shared by LAN and WiFi (separate from the WiFi-specific ones above)
        site_ready_for_impl: '',
        site_readiness_date: '',
        wifi_fe_required: '',
        floor_dimension_sqm: '',
        floors_in_scope: '',
        ceiling_height: '',
        area_in_scope: '',
        device_type_count: '',
        coverage_in_scope: '',
        coverage_out_of_scope: '',
        critical_areas: '',
        data_usage_type: '',
        wall_types: '',

        num_premium_phones: '',
        num_analogue_phones: '',
        voice_vc_devices: '',
        num_conference_devices: '',
        new_ddi_required: false,
        ddi_solution: '',
        ddi_count: '',
        ddi_ported_count: '',
        sip_trunk_local_gw: '',
        ivr_required: '',
        hunt_group_required: '',
        pickup_group_required: '',

        request_description: '',
        business_reasons: '',

        customer_name: userName,
        customer_contact_mail: userRole?.userEmail || '',
        customer_contact_phone: '',
        technical_contact_name: '',
        technical_contact_email: '',
        technical_contact_phone: '',
    });

    const [manualWanLink, setManualWanLink] = useState(false);
    const [manualCircuits, setManualCircuits] = useState(false);
    const [demandLookup, setDemandLookup] = useState(null); // null | 'loading' | { found, error }
    const [manualBw, setManualBw] = useState(false);

    const [countryCityData, setCountryCityData] = useState({ countries: [], cities: {}, country_names: {}, city_names: {} });
    const [countryCityLoading, setCountryCityLoading] = useState(false);
    const [siteIdOptions, setSiteIdOptions] = useState([]);
    const [siteIdOptionsLoading, setSiteIdOptionsLoading] = useState(false);
    const [temLoading, setTemLoading] = useState(false);
    const [temData, setTemData] = useState(null);
    const [locationIdLoading, setLocationIdLoading] = useState(false);
    const [locationIdFound, setLocationIdFound] = useState(null);

    const [wanEstimate, setWanEstimate] = useState(null);
    const [wanEstimateLoading, setWanEstimateLoading] = useState(false);
    const [lanEstimate, setLanEstimate] = useState(null);
    const [wifiEstimate, setWifiEstimate] = useState(null);

    const [custPhoneError, setCustPhoneError] = useState('');
    const [techEmailError, setTechEmailError] = useState('');
    const [techPhoneError, setTechPhoneError] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [savedId, setSavedId] = useState(updateId ? Number(updateId) : null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState('');
    const [dialogMessage, setDialogMessage] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);
    const [bwRedirectOpen, setBwRedirectOpen] = useState(false);
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
    const [quoteReview, setQuoteReview] = useState(null);

    const odmTitle = watch ? watch('odmTitle') : undefined;
    const grmDemandNumber = watch ? watch('grmDemandNumber') : undefined;

    const [fieldErrors, setFieldErrors] = useState({});
    const clearFieldError = (name) => setFieldErrors((prev) => (prev[name] ? { ...prev, [name]: false } : prev));

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLocalData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        clearFieldError(name);
    };

    const handlePhoneChange = (name, setError) => (e) => {
        const { value } = e.target;
        setLocalData((prev) => ({ ...prev, [name]: value }));
        setError(validatePhone(value) || '');
        clearFieldError(name);
    };

    const handleEmailChange = (name, setError) => (e) => {
        const { value } = e.target;
        setLocalData((prev) => ({ ...prev, [name]: value }));
        setError(validateEmail(value) || '');
        clearFieldError(name);
    };

    // ── Country / City / Site ID — same flow as Circuit Ordering's existing-site
    // path (Country -> City -> Site ID Autocomplete), reusing the same real TEM
    // inventory endpoints. A Site Expansion site is, by definition, one that
    // already has TEM records, so there is no "new site" branch here. ──
    useEffect(() => {
        const load = async () => {
            setCountryCityLoading(true);
            try {
                const res = await ApiService.siteExpansionTemCountriesCities();
                setCountryCityData({
                    countries: res.countries || [],
                    cities: res.cities || {},
                    country_names: res.country_names || {},
                    city_names: res.city_names || {},
                });
            } catch (e) {
                console.error('Failed to load countries/cities:', e);
            } finally {
                setCountryCityLoading(false);
            }
        };
        load();
    }, []);

    const handleSiteIdOpen = async () => {
        setSiteIdOptionsLoading(true);
        try {
            const res = await ApiService.siteExpansionTemSiteIds(localData.country, localData.city);
            setSiteIdOptions(res.site_ids || []);
        } catch (e) {
            console.error('Failed to load site IDs:', e);
        } finally {
            setSiteIdOptionsLoading(false);
        }
    };

    const handleSiteIdSelect = async (siteId) => {
        clearFieldError('site_id');
        setLocalData((prev) => ({
            ...prev, site_id: siteId || '', site_address: '',
            primary_location_id: '', existing_bandwidth_mbps: '', num_existing_wan_circuits: '',
        }));
        setTemData(null);
        setLocationIdFound(null);
        if (!siteId) return;

        setTemLoading(true);
        setLocationIdLoading(true);
        ApiService.siteExpansionGetLocationId(siteId)
            .then((res) => {
                setLocalData((prev) => ({ ...prev, primary_location_id: res.found ? res.location_id : '' }));
                setLocationIdFound(!!res.found);
                if (res.found) clearFieldError('primary_location_id');
            })
            .catch(() => setLocationIdFound(false))
            .finally(() => setLocationIdLoading(false));

        try {
            const result = await ApiService.siteExpansionTemLookup(siteId);
            if (result.found && result.records && result.records.length > 0) {
                const first = result.records[0];
                const totalBw = result.records.reduce((sum, r) => {
                    const bw = parseFloat(_findRowValue(r.row_data, [
                        'Port Bandwidth (in Mbps )', 'Access Bandwidth (in Mbps )', 'Bandwidth',
                    ]));
                    return sum + (isNaN(bw) ? 0 : bw);
                }, 0);
                setTemData({ found: true, records: result.records });
                setLocalData((prev) => ({
                    ...prev,
                    site_id: siteId,
                    site_address: first.site_address || '',
                    existing_bandwidth_mbps: totalBw || prev.existing_bandwidth_mbps,
                    num_existing_wan_circuits: result.records.length,
                }));
            } else {
                setTemData({ found: false });
            }
        } catch (e) {
            setTemData({ found: false, error: e.message });
        } finally {
            setTemLoading(false);
        }
    };

    // ── Bandwidth auto-calc: 2 Mbps/user + 0.064 Mbps/phone + 3 Mbps/VC, +10% buffer ──
    useEffect(() => {
        if (manualBw) return;
        const users = parseFloat(localData.num_users) || 0;
        const phones = parseFloat(localData.num_phones) || 0;
        const vc = parseFloat(localData.num_vc_devices) || 0;
        if (!users && !phones && !vc) return;
        const raw = users * 2 + phones * 0.064 + vc * 3;
        setLocalData((prev) => ({ ...prev, required_bandwidth_mbps: Math.ceil(raw * 1.1) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.num_users, localData.num_phones, localData.num_vc_devices, manualBw]);

    // ── Existing bandwidth / circuit count from an already-ordered circuit's demand number ──
    useEffect(() => {
        const demand = (localData.circuit_demand_number || '').trim();
        if (!localData.circuit_already_ordered || !demand) {
            setDemandLookup(null);
            return undefined;
        }
        let cancelled = false;
        setDemandLookup('loading');
        const timer = setTimeout(() => {
            ApiService.siteExpansionCircuitDemandLookup(demand)
                .then((res) => {
                    if (cancelled) return;
                    if (res.found) {
                        setLocalData((prev) => ({
                            ...prev,
                            existing_bandwidth_mbps: res.bandwidth_mbps ?? prev.existing_bandwidth_mbps,
                            num_existing_wan_circuits: res.num_circuits ?? prev.num_existing_wan_circuits,
                        }));
                    }
                    setDemandLookup({ found: !!res.found });
                })
                .catch((e) => { if (!cancelled) setDemandLookup({ found: false, error: e.message }); });
        }, 600);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [localData.circuit_already_ordered, localData.circuit_demand_number]);


    // ── Drop survey attachments that no longer apply when the WiFi survey answer changes ──
    useEffect(() => {
        const none = (slot) => ({ [`${slot}_file_name`]: '', [`${slot}_file_base64`]: '', [`${slot}_file_size`]: null });
        if (localData.wifi_survey_done === 'No') setLocalData((prev) => (prev.heatmap_file_name || prev.survey_result_file_name ? { ...prev, ...none('heatmap'), ...none('survey_result') } : prev));
    }, [localData.wifi_survey_done]);

    // ── Required circuits = existing + additional links ──
    useEffect(() => {
        if (localData.wan_change_type !== 'Additional Link' || localData.num_additional_links === '') return;
        const total = (parseInt(localData.num_existing_wan_circuits, 10) || 0) + (parseInt(localData.num_additional_links, 10) || 0);
        setManualCircuits(true);
        setLocalData((prev) => (String(prev.num_wan_circuits_required) === String(total) ? prev : { ...prev, num_wan_circuits_required: total }));
    }, [localData.wan_change_type, localData.num_additional_links, localData.num_existing_wan_circuits]);

    // ── WAN link type suggestion from site type / user count ──
    useEffect(() => {
        if (manualWanLink) return;
        const users = parseFloat(localData.num_users) || 0;
        let combo;
        if (localData.site_type === 'Data Center') combo = WAN_LINK_COMBOS[0];
        else if (users > 50) combo = WAN_LINK_COMBOS[0];
        else if (users >= 5) combo = WAN_LINK_COMBOS[1];
        else combo = WAN_LINK_COMBOS[2];
        setLocalData((prev) => ({
            ...prev,
            link_type: combo.value,
            num_wan_circuits_required: manualCircuits ? prev.num_wan_circuits_required : combo.circuits,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.site_type, localData.num_users, manualWanLink, manualCircuits]);

    const handleManualWanLink = (value) => {
        setManualWanLink(true);
        setManualCircuits(false);
        const combo = WAN_LINK_COMBOS.find((c) => c.value === value);
        setLocalData((prev) => ({ ...prev, link_type: value, num_wan_circuits_required: combo ? combo.circuits : prev.num_wan_circuits_required }));
    };

    // ── Live WAN cost estimate ──
    useEffect(() => {
        if (!localData.wan_in_scope || localData.wan_change_type === 'Bandwidth Upgrade' || !localData.country || !localData.required_bandwidth_mbps) {
            setWanEstimate(null);
            return;
        }
        let cancelled = false;
        setWanEstimateLoading(true);
        ApiService.siteExpansionCostEstimate('wan', {
            country: localData.country,
            site_type: localData.site_type,
            required_bandwidth_mbps: localData.required_bandwidth_mbps,
            num_wan_circuits_required: localData.num_wan_circuits_required || 2,
        })
            .then((res) => { if (!cancelled) setWanEstimate(res); })
            .catch(() => { if (!cancelled) setWanEstimate(null); })
            .finally(() => { if (!cancelled) setWanEstimateLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.wan_in_scope, localData.wan_change_type, localData.country, localData.site_type, localData.required_bandwidth_mbps, localData.num_wan_circuits_required]);

    // ── Live LAN cost estimate ──
    useEffect(() => {
        if (!localData.lan_in_scope || !localData.country) {
            setLanEstimate(null);
            return;
        }
        let cancelled = false;
        ApiService.siteExpansionCostEstimate('lan', {
            country: localData.country,
            num_users: localData.num_users || 0,
            num_phones: localData.num_phones || 0,
            num_vc_devices: localData.num_vc_devices || 0,
            num_static_devices: localData.num_static_devices || 0,
            floor_dimension_sqm: localData.floor_dimension_sqm || 0,
        })
            .then((res) => { if (!cancelled) setLanEstimate(res); })
            .catch(() => { if (!cancelled) setLanEstimate(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.lan_in_scope, localData.country, localData.num_users, localData.num_phones, localData.num_vc_devices, localData.num_static_devices, localData.floor_dimension_sqm]);

    // ── Live WiFi cost estimate ──
    useEffect(() => {
        if (!localData.wifi_in_scope || !localData.country || !localData.floor_dimension_sqm) {
            setWifiEstimate(null);
            return;
        }
        let cancelled = false;
        ApiService.siteExpansionCostEstimate('wifi', {
            country: localData.country,
            floor_dimension_sqm: localData.floor_dimension_sqm,
        })
            .then((res) => { if (!cancelled) setWifiEstimate(res); })
            .catch(() => { if (!cancelled) setWifiEstimate(null); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localData.wifi_in_scope, localData.country, localData.floor_dimension_sqm]);

    // ── Pre-fill GRM-provided fields when arriving via GRM redirect ──
    useEffect(() => {
        if (!externalDemandData) return;
        setLocalData((prev) => ({
            ...prev,
            cost_centre: externalDemandData.cost_centre || prev.cost_centre,
            debtor: externalDemandData.debtor || prev.debtor,
            oe_name: externalDemandData.oe_name || prev.oe_name,
            request_description: externalDemandData.description_of_odm_request || prev.request_description,
        }));
    }, [externalDemandData]);

    useEffect(() => {
        if (userRole?.userEmail) {
            setLocalData((prev) => ({ ...prev, customer_contact_mail: userRole.userEmail }));
        }
    }, [userRole?.userEmail]);

    // ── Load existing request in update mode ──
    useEffect(() => {
        if (!isUpdateMode || !updateId) return;
        const load = async () => {
            try {
                const data = await ApiService.getSiteExpansionRequest(updateId);
                setLocalData((prev) => ({ ...prev, ...data }));
                setManualBw(true);
                setManualWanLink(true);
            } catch (e) {
                console.error('Error loading site expansion request:', e);
            }
        };
        load();
    }, [isUpdateMode, updateId]);

    const _toFloat = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };
    const _toInt = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };

    const buildPayload = () => ({
        ...localData,
        // A bandwidth upgrade is raised as its own request, so this one carries no WAN scope.
        wan_in_scope: !!localData.wan_in_scope && localData.wan_change_type !== 'Bandwidth Upgrade',
        odm_title: odmTitle || '',
        grm_demand_number: grmDemandNumber || '',
        oe_name: localData.oe_name || userName,
        num_users: _toInt(localData.num_users),
        num_phones: _toInt(localData.num_phones),
        num_vc_devices: _toInt(localData.num_vc_devices),
        existing_bandwidth_mbps: _toFloat(localData.existing_bandwidth_mbps),
        num_existing_wan_circuits: _toInt(localData.num_existing_wan_circuits),
        num_additional_links: localData.wan_change_type === 'Additional Link' ? _toInt(localData.num_additional_links) : null,
        required_bandwidth_mbps: _toFloat(localData.required_bandwidth_mbps),
        num_wan_circuits_required: _toInt(localData.num_wan_circuits_required),
        num_floors: _toInt(localData.num_floors),
        num_static_devices: _toInt(localData.num_static_devices),
        num_comm_rooms: _toInt(localData.num_comm_rooms),
        num_users_per_floor: _toInt(localData.num_users_per_floor),
        floor_dimension_sqm: _toFloat(localData.floor_dimension_sqm),
        num_premium_phones: _toInt(localData.num_premium_phones),
        num_analogue_phones: _toInt(localData.num_analogue_phones),
        voice_vc_devices: _toInt(localData.voice_vc_devices),
        num_conference_devices: _toInt(localData.num_conference_devices),
        ddi_count: _toInt(localData.ddi_count),
        ddi_ported_count: _toInt(localData.ddi_ported_count),
    });

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = buildPayload();
            if (isUpdateMode && updateId) {
                await ApiService.updateSiteExpansionRequest(updateId, payload);
                setDialogTitle('Saved');
                setDialogMessage('Site expansion draft updated successfully.');
            } else {
                const result = await ApiService.createSiteExpansionRequest(payload);
                setSavedId(result.id);
                setDialogTitle('Saved');
                setDialogMessage('Site expansion request saved as draft.');
            }
            ApiService.invalidateRequestsCache && ApiService.invalidateRequestsCache();
            setDialogOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Failed to save draft: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleFormSubmit = async () => {
        const missing = [];
        const errors = {};
        const require = (field, label) => {
            if (!localData[field]) { missing.push(label); errors[field] = true; }
        };
        require('oe_name', 'OE Name');
        require('debtor', 'Debtor');
        require('cost_centre', 'Cost Centre / WBS');
        require('site_id', 'Existing Site ID');
        require('country', 'Country (from Existing Site ID lookup)');
        require('primary_location_id', 'Location ID');
        require('desired_installation_date', 'Desired Installation Date');
        require('request_description', 'Description of ODM Request');
        require('customer_contact_phone', 'Customer Contact Phone');
        if (localData.wifi_in_scope && !localData.floor_plan_file_name) { missing.push('Floor Plan attachment'); errors.floor_plan = true; }
        if ((localData.lan_in_scope || localData.wifi_in_scope) && !localData.survey_file_name) { missing.push('General Survey attachment'); errors.survey = true; }
        setFieldErrors(errors);
        if (missing.length > 0) {
            setDialogTitle('Validation Error');
            setDialogMessage(`Please fill in all required fields: ${missing.join(', ')}.`);
            setDialogOpen(true);
            return;
        }
        const phoneErr = validatePhone(localData.customer_contact_phone);
        if (phoneErr) {
            setFieldErrors((prev) => ({ ...prev, customer_contact_phone: true }));
            setDialogTitle('Validation Error');
            setDialogMessage('Please correct the Customer Contact Phone format: +XX (XXX) XXX-XXXX');
            setDialogOpen(true);
            return;
        }

        setIsSubmitting(true);
        try {
            let id = savedId;
            const payload = buildPayload();
            if (id) {
                await ApiService.updateSiteExpansionRequest(id, payload);
            } else {
                const created = await ApiService.createSiteExpansionRequest(payload);
                id = created.id;
                setSavedId(id);
            }
            const review = await ApiService.siteExpansionQuoteReview(id).catch(() => null);
            setQuoteReview(review);
            setConfirmSubmitOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Failed to prepare submission: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmedSubmit = async () => {
        setConfirmSubmitOpen(false);
        setIsSubmitting(true);
        try {
            await ApiService.submitSiteExpansionRequest(savedId);
            ApiService.invalidateRequestsCache && ApiService.invalidateRequestsCache();
            setSuccessOpen(true);
        } catch (e) {
            setDialogTitle('Error');
            setDialogMessage('Submission failed: ' + e.message);
            setDialogOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        if (dialogTitle === 'Saved') navigate('/track-requests');
    };

    const tf = (name, label, extra = {}) => (
        <TextField
            fullWidth
            sx={FIELD_SX}
            name={name}
            label={label}
            value={localData[name]}
            onChange={handleChange}
            error={!!fieldErrors[name]}
            {...extra}
        />
    );

    const sel = (name, label, options, extra = {}) => (
        <TextField
            select
            fullWidth
            sx={FIELD_SX}
            name={name}
            label={label}
            value={localData[name]}
            onChange={handleChange}
            error={!!fieldErrors[name]}
            {...extra}
        >
            <MenuItem value="">— Select —</MenuItem>
            {options.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
    );

    const chk = (name, label) => (
        <FormControlLabel
            control={<Checkbox checked={!!localData[name]} onChange={handleChange} name={name} />}
            label={label}
        />
    );

    const handleFileSelect = (slot) => (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = String(reader.result).split(',')[1] || '';
            setLocalData((prev) => ({
                ...prev,
                [`${slot}_file_name`]: file.name,
                [`${slot}_file_base64`]: base64,
                [`${slot}_file_size`]: file.size,
            }));
        };
        reader.readAsDataURL(file);
    };

    const fileField = (slot, label) => (
        <Box>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button variant="outlined" component="label" size="small" sx={{ flexShrink: 0 }}>
                    Attach
                    <input type="file" hidden onChange={handleFileSelect(slot)} />
                </Button>
                <Typography
                    variant="body2"
                    color={localData[`${slot}_file_name`] ? 'text.primary' : 'text.secondary'}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {localData[`${slot}_file_name`] || 'No file selected'}
                </Typography>
            </Box>
        </Box>
    );

    const fmtEuro = (v) => `€${(Number(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <>
            <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Location ID required:</strong> A valid Primary Location ID is mandatory to submit this request.
                If the site doesn't have one yet, create it in ServiceNow (Self-Service &gt; Service Catalog &gt; SIM Services catalogue) before proceeding —{' '}
                <a
                    href="https://aztech.service-now.com/nav_to.do?uri=%2Fcom.glideapp.servicecatalog_cat_item_view.do%3Fv%3D1%26sysparm_id%3Dd9e10dabb0038b802f1065dfe0588b68%26sysparm_link_parent%3D88b14d6bb0038b802f1065dfe0588b32%26sysparm_catalog%3De0d08b13c3330100c8b837659bba8fb4%26sysparm_catalog_view%3Dcatalog_default%26sysparm_view%3Dcatalog_default"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#663c00', fontWeight: 700, textDecoration: 'underline' }}
                >
                    Click here to create one
                </a>.
            </Alert>

            {/* ── Site Details ───────────────────────────────────────────── */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Site Details</Typography>

                <Row>
                    <F>{tf('oe_name', 'OE Name *', { placeholder: 'e.g. Allianz Technology' })}</F>
                    <F>{tf('debtor', 'Debtor *')}</F>
                </Row>
                <Row>
                    <F>{tf('cost_centre', 'Cost Centre / WBS *')}</F>
                    <F>{sel('site_type', 'Site Type *', SITE_TYPE_OPTIONS, { helperText: 'Drives the SD-WAN Management and AzT effort tiers' })}</F>
                </Row>

                <Row>
                    {/* ── Country ── */}
                    <F>
                        <FormControl fullWidth variant="outlined" sx={FIELD_SX}>
                            <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>Country *</InputLabel>
                            <Select
                                name="country"
                                value={localData.country || ''}
                                onChange={(e) => {
                                    setLocalData((prev) => ({ ...prev, country: e.target.value, city: '', site_id: '' }));
                                    setSiteIdOptions([]);
                                    setTemData(null);
                                    clearFieldError('country');
                                }}
                                label="Country *"
                                notched
                                displayEmpty
                                error={!!fieldErrors.country}
                                disabled={countryCityLoading}
                                MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
                            >
                                {countryCityData.countries.map((c) => {
                                    const fullName = countryCityData.country_names[c];
                                    return <MenuItem key={c} value={c}>{fullName ? `${fullName} (${c})` : c}</MenuItem>;
                                })}
                            </Select>
                            <FormHelperText>{countryCityLoading ? 'Loading…' : ' '}</FormHelperText>
                        </FormControl>
                    </F>

                    {/* ── City ── */}
                    <F>
                        <FormControl fullWidth variant="outlined" sx={FIELD_SX}>
                            <InputLabel shrink sx={{ backgroundColor: '#fff', px: 0.5 }}>City *</InputLabel>
                            <Select
                                name="city"
                                value={localData.city || ''}
                                onChange={(e) => {
                                    setLocalData((prev) => ({ ...prev, city: e.target.value, site_id: '' }));
                                    setSiteIdOptions([]);
                                    setTemData(null);
                                }}
                                label="City *"
                                notched
                                displayEmpty
                                disabled={!localData.country}
                                MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
                            >
                                {(countryCityData.cities[localData.country] || []).map((c) => {
                                    const fullName = (countryCityData.city_names[localData.country] || {})[c];
                                    return <MenuItem key={c} value={c}>{fullName ? `${fullName} (${c})` : c}</MenuItem>;
                                })}
                            </Select>
                            <FormHelperText>{!localData.country ? 'Select a country first' : ' '}</FormHelperText>
                        </FormControl>
                    </F>
                </Row>

                <Row>
                    {/* ── Existing Site ID (Autocomplete, populated from TEM once Country/City are set) ── */}
                    <F>
                        <Autocomplete
                            options={siteIdOptions}
                            value={localData.site_id || null}
                            onOpen={handleSiteIdOpen}
                            onChange={(_, newValue) => handleSiteIdSelect(newValue)}
                            loading={siteIdOptionsLoading}
                            disabled={!localData.country || !localData.city}
                            filterOptions={(options, { inputValue }) =>
                                options.filter((o) => o.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 100)
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    sx={FIELD_SX}
                                    label="Existing Site ID *"
                                    placeholder="Type to search site IDs"
                                    error={!!fieldErrors.site_id}
                                    helperText={
                                        temLoading ? 'Loading site details…'
                                            : temData?.found ? `Found ${temData.records.length} circuit(s) — Address/Bandwidth auto-filled below`
                                            : temData?.found === false ? 'No TEM records found for this Site ID'
                                            : !localData.country || !localData.city ? 'Select Country and City first'
                                            : ' '
                                    }
                                    FormHelperTextProps={{
                                        sx: {
                                            color: temData?.found ? 'success.main' : temData?.found === false ? 'error.main' : undefined,
                                            minHeight: '1.25em',
                                        },
                                    }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {siteIdOptionsLoading || temLoading ? <CircularProgress size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            ListboxProps={{ style: { maxHeight: 250 } }}
                        />
                    </F>
                    <F>
                        <TextField
                            fullWidth variant="outlined"
                            label="Location ID *"
                            name="primary_location_id"
                            value={localData.primary_location_id ?? ''}
                            InputLabelProps={{ shrink: true }}
                            error={!!fieldErrors.primary_location_id}
                            InputProps={{
                                readOnly: true,
                                endAdornment: locationIdLoading ? <CircularProgress size={18} /> : null,
                            }}
                            helperText={
                                locationIdLoading ? 'Looking up Location ID...' :
                                locationIdFound === true ? '✓ Auto-populated from Site ID mapping' :
                                locationIdFound === false ? 'No Location ID mapping found for this Site ID' :
                                'Auto-populated when a Site ID is selected'
                            }
                            FormHelperTextProps={{
                                sx: {
                                    color: fieldErrors.primary_location_id ? 'error.main' :
                                           locationIdFound === true ? 'success.main' :
                                           locationIdFound === false ? 'text.secondary' : undefined,
                                    minHeight: '1.25em',
                                }
                            }}
                            sx={{
                                ...FIELD_SX,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: locationIdFound === true ? '#f0fdf4' : undefined,
                                },
                            }}
                        />
                    </F>
                </Row>

                <Row>
                    <F>{tf('site_address', 'Site Address', {
                        multiline: true, rows: 2,
                        helperText: 'Auto-filled from TEM lookup (Address a column), or enter manually',
                    })}</F>
                    <F>{tf('desired_installation_date', 'Desired Installation Date *', { type: 'date', InputLabelProps: { shrink: true } })}</F>
                </Row>

                <Row>
                    <F>{chk('is_colocated', 'Is this a co-located site?')}</F>
                    {localData.is_colocated && (
                        <F>{tf('colocated_oes', 'Other OEs at this Site', {
                            helperText: 'Comma-separated. A separate ODM must be raised for each additional OE.',
                        })}</F>
                    )}
                </Row>
            </Paper>

            {/* ── Services Required ──────────────────────────────────────── */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Services Required</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Indicate which services are in scope for this site expansion.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[
                        ['wan_in_scope', 'WAN', 'SD-WAN circuits, bandwidth'],
                        ['lan_in_scope', 'LAN', 'Access switches, ports'],
                        ['wifi_in_scope', 'WiFi', 'Access points, survey'],
                        ['voice_in_scope', 'Voice', 'Phones, DDI, IVR'],
                    ].map(([name, label, desc]) => (
                        <Box
                            key={name}
                            onClick={() => setLocalData((prev) => ({ ...prev, [name]: !prev[name] }))}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                border: '1.5px solid', borderColor: localData[name] ? 'primary.main' : '#e0e0e0',
                                borderRadius: 2, px: 1.75, py: 1, cursor: 'pointer', userSelect: 'none',
                                backgroundColor: localData[name] ? '#f0f4ff' : 'transparent',
                                transition: 'all 0.15s',
                            }}
                        >
                            <Checkbox
                                checked={!!localData[name]}
                                onChange={() => setLocalData((prev) => ({ ...prev, [name]: !prev[name] }))}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{ p: 0.5, color: 'primary.main', '&.Mui-checked': { color: 'primary.main' } }}
                            />
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', lineHeight: 1.3 }}>{label}</Typography>
                                <Typography sx={{ fontSize: 11, color: '#888', lineHeight: 1.3 }}>{desc}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* ── WAN ─────────────────────────────────────────────────────── */}
            {localData.wan_in_scope && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Network Services Requirements — WAN</Typography>

                    <Row>
                        <F>{chk('circuit_already_ordered', 'Circuit Already Ordered?')}</F>
                        {localData.circuit_already_ordered && (
                            <F>{tf('circuit_demand_number', 'Circuit Demand Number', {
                                helperText: demandLookup === 'loading' ? 'Looking up circuit order…'
                                    : demandLookup && demandLookup.found ? 'Existing bandwidth and circuits filled from this circuit order — edit if needed'
                                    : demandLookup && !demandLookup.found ? (demandLookup.error || 'No circuit order found for this demand number — enter the existing values manually')
                                    : 'Demand number from the circuit order',
                            })}</F>
                        )}
                    </Row>

                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Current State</Typography>
                    <Row>
                        <F>{tf('existing_bandwidth_mbps', 'Existing Bandwidth (Mbps)', { type: 'number', helperText: 'Auto-filled from Existing Site ID or Circuit Demand Number; edit if needed' })}</F>
                        <F>{tf('num_existing_wan_circuits', 'Number of Existing WAN Circuits', { type: 'number', helperText: 'Auto-filled from Existing Site ID or Circuit Demand Number; edit if needed' })}</F>
                    </Row>

                    <Row>
                        <F>{sel('wan_change_type', 'WAN Change Needed *', ['Additional Link', 'Bandwidth Upgrade'], { helperText: 'Additional link: continue here. Bandwidth upgrade: raised on the Bandwidth Upgrade form' })}</F>
                        {localData.wan_change_type === 'Additional Link' && (
                            <F>{tf('num_additional_links', 'Number of Additional Links Needed *', { type: 'number', inputProps: { min: 1, step: 1 }, helperText: 'Added to the existing WAN circuits to give the circuits required' })}</F>
                        )}
                    </Row>

                    {localData.wan_change_type === 'Bandwidth Upgrade' && (
                        <Alert
                            severity="info"
                            sx={{ mb: 2 }}
                            action={<Button color="inherit" size="small" onClick={() => setBwRedirectOpen(true)}>Open form</Button>}
                        >
                            Increasing the bandwidth of existing circuits is raised as a Bandwidth Upgrade/Downgrade request. "Open form" takes you straight to it —
                            no WAN cost is included in this Site Expansion request. Your changes here are not saved when you leave; use Save first if you need them.
                        </Alert>
                    )}

                    {localData.wan_change_type !== 'Bandwidth Upgrade' && (<>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Requested State</Typography>
                    <Row>
                        <F>{tf('num_wan_circuits_required', 'Number of WAN Circuits Required', {
                            type: 'number',
                            inputProps: { min: 1, step: 1 },
                            helperText: 'Derived from Link Type; edit to override',
                            onChange: (e) => { setManualCircuits(true); handleChange(e); },
                        })}</F>
                        <F grow={2}>
                            <TextField
                                select fullWidth sx={FIELD_SX} name="link_type" label="Link Type"
                                value={localData.link_type}
                                onChange={(e) => handleManualWanLink(e.target.value)}
                                helperText="Suggested from Site Type / Number of Users — edit if needed"
                            >
                                {WAN_LINK_COMBOS.map((c) => <MenuItem key={c.value} value={c.value}>{c.value}</MenuItem>)}
                            </TextField>
                        </F>
                    </Row>
                    <Row>
                        <F>{chk('wan_fe_required', 'Field Engineer Required for Onsite Activity?')}</F>
                    </Row>

                    <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc', mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: '#003781', fontWeight: 700 }}>Bandwidth Estimation</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            Fill the counts below to auto-calculate the requested bandwidth (2 Mbps/user, 0.064 Mbps/phone, 3 Mbps/VC device + 10% future-expansion buffer). You can also enter bandwidth directly if already known.
                        </Typography>
                        <Row>
                            <F>{tf('num_users', 'Number of Users', { type: 'number', helperText: '2 Mbps per user' })}</F>
                            <F>{tf('num_phones', 'Number of Phones', { type: 'number', helperText: '0.064 Mbps per phone' })}</F>
                            <F>{tf('num_vc_devices', 'Number of VC Devices', { type: 'number', helperText: '3 Mbps per device' })}</F>
                        </Row>
                        <Row>
                            <F>
                                <TextField
                                    fullWidth sx={FIELD_SX} type="number" name="required_bandwidth_mbps" label="Required Bandwidth (Mbps)"
                                    value={localData.required_bandwidth_mbps}
                                    onChange={(e) => { setManualBw(true); handleChange(e); }}
                                    helperText={manualBw ? 'Manually entered — counts above no longer override' : 'Auto-calculated (10% buffer) — edit to override'}
                                />
                            </F>
                        </Row>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc' }}>
                        <Typography variant="subtitle2" color="primary">WAN Cost Estimate</Typography>
                        {wanEstimateLoading && <CircularProgress size={20} sx={{ mt: 1 }} />}
                        {!wanEstimateLoading && wanEstimate && wanEstimate.found && (
                            <>
                                <Typography variant="h5" color="primary" sx={{ my: 1 }}>{fmtEuro(wanEstimate.total)}</Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableBody>
                                            {Object.entries(wanEstimate.breakdown || {}).map(([k, v]) => (
                                                <TableRow key={k}>
                                                    <TableCell>{k}</TableCell>
                                                    <TableCell align="right">{fmtEuro(v)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Typography variant="caption" color="text.secondary">
                                    Router: {wanEstimate.router_model} · Provider: {wanEstimate.provider} · {wanEstimate.upgrade_type} upgrade
                                </Typography>
                            </>
                        )}
                        {!wanEstimateLoading && wanEstimate && !wanEstimate.found && (
                            <Alert severity="warning" sx={{ mt: 1 }}>{wanEstimate.message || 'No pricing data found for this country.'}</Alert>
                        )}
                        {!wanEstimateLoading && !wanEstimate && (
                            <Typography variant="body2" color="text.secondary">Enter Country and Required Bandwidth to see an estimate.</Typography>
                        )}
                    </Box>
                    </>)}
                </Paper>
            )}

            {/* ── General survey attachment (shared by LAN and WiFi) ───────── */}
            {(localData.lan_in_scope || localData.wifi_in_scope) && (
                <Paper sx={{ p: 3, mb: 3, border: fieldErrors.survey ? '1px solid #d32f2f' : undefined }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Site Survey — {[localData.lan_in_scope && 'LAN', localData.wifi_in_scope && 'WiFi'].filter(Boolean).join(' & ')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        One general survey attachment, shared by LAN and WiFi. WiFi-specific attachments (floor plan, heatmap, survey result) stay in the WiFi section.
                    </Typography>
                    <Row sx={{ mb: 0 }}>
                        <F>{fileField('survey', 'Attach General Survey *')}</F>
                    </Row>
                </Paper>
            )}

            {/* ── LAN ─────────────────────────────────────────────────────── */}
            {localData.lan_in_scope && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Network Services Requirements — LAN</Typography>
                    <Row>
                        <F>{tf('num_floors', 'Number of Floors', { type: 'number' })}</F>
                        <F>{tf('num_static_devices', 'Static Devices / Printers / Servers', { type: 'number' })}</F>
                        <F>{tf('num_comm_rooms', 'Communication Room(s)', { type: 'number' })}</F>
                    </Row>
                    <Row>
                        <F>{chk('lan_fe_required', 'Field Engineer Required for Onsite Activity?')}</F>
                        <F>{tf('num_users_per_floor', 'Number of Users per Floor', { type: 'number' })}</F>
                    </Row>
                    <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc' }}>
                        <Typography variant="subtitle2" color="primary">LAN Setup Upgrade + LAN Access</Typography>
                        {lanEstimate ? (
                            <>
                                <Typography variant="h6" color="primary" sx={{ my: 1 }}>
                                    {fmtEuro(lanEstimate.lan_access_mrc)}/mo + {fmtEuro(lanEstimate.lan_setup_otc)} one-time
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    LAN Access — {lanEstimate.access_switches} access switch(es) × {fmtEuro(lanEstimate.lan_access_unit_price)} (Country List {lanEstimate.country_list}),
                                    from {lanEstimate.lan_ports} LAN ports (incl. 25% spare); LAN Setup Upgrade — {fmtEuro(lanEstimate.lan_setup_otc)} one-time
                                    ({lanEstimate.core_switches} core switch(es))
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Enter Country (via Existing Site ID) to see an estimate.</Typography>
                        )}
                    </Box>
                </Paper>
            )}

            {/* ── WiFi ────────────────────────────────────────────────────── */}
            {localData.wifi_in_scope && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Network Services Requirements — WiFi</Typography>
                    <Row>
                        <F>{sel('wifi_survey_done', 'WiFi Survey Completed? *', YES_NO)}</F>
                        <F>{tf('floor_dimension_sqm', 'Floor Dimension (Square Meter) *', { type: 'number' })}</F>
                    </Row>
                    <Row>
                        {localData.wifi_survey_done === 'No' && (
                            <F>{tf('wifi_survey_planned_date', 'If WiFi Survey Not Completed — Planned Date', { type: 'date', InputLabelProps: { shrink: true }, helperText: 'Expected date the WiFi survey will be completed' })}</F>
                        )}
                        <F>{fileField('floor_plan', 'Attach Floor Plan *')}</F>
                        {localData.wifi_survey_done === 'Yes' && (<>
                            <F>{fileField('heatmap', 'Attach Heatmap *')}</F>
                            <F>{fileField('survey_result', 'Attach Survey Result *')}</F>
                        </>)}
                    </Row>

                    {localData.wifi_survey_done === 'No' && (
                    <Box sx={{ border: '1px dashed #b9c6dd', borderRadius: 1, p: 2, mb: 2.5, bgcolor: '#fbfcfe' }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>Wi-Fi Heatmap — Site Information Required</Typography>
                        <Row>
                            <F>{tf('floors_in_scope', 'Floor in Scope', { placeholder: 'e.g. Floor 1, Floor 2, Ground Floor' })}</F>
                            <F>{tf('ceiling_height', 'Ceiling Height', { placeholder: 'e.g. 2.8m (3.5m in atrium)' })}</F>
                        </Row>
                        <Row>
                            <F>{tf('area_in_scope', 'Area in Scope', { placeholder: 'e.g. Open office, meeting rooms, reception' })}</F>
                            <F>{tf('critical_areas', 'Critical Areas', { placeholder: 'e.g. Boardroom, Town Hall' })}</F>
                        </Row>
                        <Row>
                            <F grow={99}>{tf('device_type_count', 'Device Types & Counts', { multiline: true, rows: 2, placeholder: 'e.g. 40 laptops, 15 desk phones, 5 tablets (Floor 1)' })}</F>
                        </Row>
                        <Row>
                            <F grow={99}>{tf('coverage_in_scope', 'Coverage in Scope', { multiline: true, rows: 2, placeholder: 'e.g. Open office, meeting rooms, corridors' })}</F>
                        </Row>
                        <Row>
                            <F grow={99}>{tf('coverage_out_of_scope', 'Coverage out of Scope', { multiline: true, rows: 2, placeholder: 'e.g. Staircases, toilets, storage, elevators' })}</F>
                        </Row>
                        <Row sx={{ mb: 0 }}>
                            <F>{sel('data_usage_type', 'Data Usage Type', DATA_USAGE_OPTIONS)}</F>
                            <F>{tf('wall_types', 'Wall Types / Pillars', { placeholder: 'e.g. Concrete walls, glass partitions' })}</F>
                        </Row>
                    </Box>
                    )}

                    <Row>
                        <F>{sel('site_ready_for_impl', 'Site Ready for Implementation? *', YES_NO)}</F>
                        <F>{sel('wifi_fe_required', 'Field Engineer Required?', YES_NO)}</F>
                        {localData.site_ready_for_impl === 'No' && (
                            <F>{tf('site_readiness_date', 'Date of Site Readiness', { type: 'date', InputLabelProps: { shrink: true } })}</F>
                        )}
                    </Row>

                    <Box sx={{ border: '1px solid #e0e7ef', borderRadius: 1, p: 2, bgcolor: '#f8f9fc' }}>
                        <Typography variant="subtitle2" color="primary">Wi-Fi Setup + Wi-Fi Access</Typography>
                        {wifiEstimate ? (
                            <>
                                <Typography variant="h6" color="primary" sx={{ my: 1 }}>
                                    {fmtEuro(wifiEstimate.wifi_access_mrc)}/mo + {fmtEuro(wifiEstimate.wifi_setup_otc)} one-time
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Wi-Fi Access — {wifiEstimate.ap_count} AP(s) × {fmtEuro(wifiEstimate.wifi_access_unit_price)} (Country List {wifiEstimate.country_list});
                                    Wi-Fi Setup — {fmtEuro(wifiEstimate.wifi_setup_otc)} one-time
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Enter Floor Dimension and Country to see an estimate.</Typography>
                        )}
                    </Box>
                </Paper>
            )}

            {/* ── Voice ───────────────────────────────────────────────────── */}
            {localData.voice_in_scope && (
                <Accordion sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Network Services Requirements — Voice</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Row>
                            <F>{tf('num_premium_phones', 'Premium Phones', { type: 'number' })}</F>
                            <F>{tf('num_analogue_phones', 'Analogue Phones / Fax', { type: 'number' })}</F>
                            <F>{tf('voice_vc_devices', 'VC Devices', { type: 'number' })}</F>
                            <F>{tf('num_conference_devices', 'Conference Devices', { type: 'number' })}</F>
                        </Row>
                        <Row>
                            <F>{sel('new_ddi_required', 'New DDI Required?', YES_NO)}</F>
                            {(localData.new_ddi_required === true || localData.new_ddi_required === 'Yes') && (
                                <F>{sel('ddi_solution', 'Preferred DDI Solution', ['PRIs', 'SIP trunk'])}</F>
                            )}
                        </Row>
                        <Row>
                            <F>{tf('ddi_count', 'Count of DDIs (Moved / Procured)', { type: 'number' })}</F>
                            <F>{tf('ddi_ported_count', 'Count of DDI to be Ported (non-AGN → AGN Env)', { type: 'number' })}</F>
                        </Row>
                        <Row sx={{ mb: 0 }}>
                            <F>{sel('ivr_required', 'IVR / ARC Console Required?', YES_NO)}</F>
                            <F>{sel('hunt_group_required', 'Hunt Group Required?', YES_NO)}</F>
                            <F>{sel('pickup_group_required', 'Pickup Group Required?', YES_NO)}</F>
                        </Row>
                    </AccordionDetails>
                </Accordion>
            )}

            {/* ── Business Justification ─────────────────────────────────── */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Business Justification</Typography>
                <Row>
                    <F grow={99}>{tf('request_description', 'Description of ODM Request *', { multiline: true, rows: 3 })}</F>
                </Row>
                <Row sx={{ mb: 0 }}>
                    <F grow={99}>{tf('business_reasons', 'Business Reasons', { multiline: true, rows: 3 })}</F>
                </Row>
            </Paper>

            {/* ── Customer Contact ───────────────────────────────────────── */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Customer Contact</Typography>
                <Row sx={{ flexWrap: 'nowrap' }}>
                    <F>{tf('customer_name', 'Name *', { disabled: true })}</F>
                    <F>{tf('customer_contact_mail', 'Email *', { disabled: true })}</F>
                    <F>
                        <TextField
                            fullWidth sx={FIELD_SX} name="customer_contact_phone" label="Customer Contact Phone *"
                            value={localData.customer_contact_phone} onChange={handlePhoneChange('customer_contact_phone', setCustPhoneError)}
                            error={!!custPhoneError} helperText={custPhoneError || 'Format: +XX (XXX) XXX-XXXX'}
                        />
                    </F>
                </Row>
                <Row sx={{ mb: 0, flexWrap: 'nowrap' }}>
                    <F>{tf('technical_contact_name', 'Technical Contact Name')}</F>
                    <F>
                        <TextField
                            fullWidth sx={FIELD_SX} name="technical_contact_email" label="Technical Contact Email"
                            value={localData.technical_contact_email} onChange={handleEmailChange('technical_contact_email', setTechEmailError)}
                            error={!!techEmailError} helperText={techEmailError}
                        />
                    </F>
                    <F>
                        <TextField
                            fullWidth sx={FIELD_SX} name="technical_contact_phone" label="Technical Contact Number"
                            value={localData.technical_contact_phone} onChange={handlePhoneChange('technical_contact_phone', setTechPhoneError)}
                            error={!!techPhoneError} helperText={techPhoneError || 'Format: +XX (XXX) XXX-XXXX'}
                        />
                    </F>
                </Row>
            </Paper>

            {/* ── Action Buttons ──────────────────────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, mb: 4, gap: 2 }}>
                <Button variant="contained" onClick={handleSave} disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} /> : null}>
                    {isSaving ? 'Saving…' : 'Save as Draft'}
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => navigate('/')}>Cancel</Button>
                <Button variant="contained" color="primary" onClick={handleFormSubmit} disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}>
                    Submit
                </Button>
            </Box>

            {/* ── Info / Error dialog ─────────────────────────────────────── */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent><Typography>{dialogMessage}</Typography></DialogContent>
                <DialogActions><Button onClick={handleDialogClose}>OK</Button></DialogActions>
            </Dialog>

            {/* ── Confirm submit dialog with combined quote ───────────────── */}
            <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{quoteReview ? 'Cost Estimate — Site Expansion' : 'Confirm Submission'}</DialogTitle>
                <DialogContent>
                    {quoteReview ? (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>Cost Breakdown</Typography>
                            <Typography variant="subtitle2" gutterBottom>
                                Total Estimated Cost:{' '}
                                <strong>{fmtEuro(quoteReview.combined_total_recurring)}/mo + {fmtEuro(quoteReview.combined_total_one_time)} one-time</strong>
                            </Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableBody>
                                        {quoteReview.estimate?.wan?.found && (
                                            <>
                                                <TableRow><TableCell>SD-WAN Circuit(s) (908138)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.sdWanCircuits)}</TableCell></TableRow>
                                                <TableRow><TableCell>SD-WAN Management (908134)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.sdWanManagement)}</TableCell></TableRow>
                                                <TableRow><TableCell>SD-WAN Router (908137)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.sdWanRouter)}</TableCell></TableRow>
                                                <TableRow><TableCell>SD-WAN IMACD — OTC Provider (908374)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.sdWanImacd)}</TableCell></TableRow>
                                                <TableRow><TableCell>Integrator Effort — Accenture (905513)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.accentureEffort)}</TableCell></TableRow>
                                                <TableRow><TableCell>SD-WAN Effort (2 PD) (908374)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.sdWanEffort)}</TableCell></TableRow>
                                                <TableRow><TableCell>ODM Team Effort (905513)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wan.breakdown?.odmTeamEffort)}</TableCell></TableRow>
                                            </>
                                        )}
                                        {quoteReview.estimate?.lan && (
                                            <>
                                                <TableRow><TableCell>LAN Setup Upgrade (one-time)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.lan.lan_setup_otc)}</TableCell></TableRow>
                                                <TableRow><TableCell>LAN Access — {quoteReview.estimate.lan.access_switches} switch(es)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.lan.lan_access_mrc)}</TableCell></TableRow>
                                            </>
                                        )}
                                        {quoteReview.estimate?.wifi && (
                                            <>
                                                <TableRow><TableCell>Wi-Fi Setup (one-time)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wifi.wifi_setup_otc)}</TableCell></TableRow>
                                                <TableRow><TableCell>Wi-Fi Access — {quoteReview.estimate.wifi.ap_count} AP(s)</TableCell><TableCell align="right">{fmtEuro(quoteReview.estimate.wifi.wifi_access_mrc)}</TableCell></TableRow>
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    ) : (
                        <Typography>Are you sure you want to submit this request?</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmSubmitOpen(false)} variant="outlined">Cancel</Button>
                    <Button onClick={handleConfirmedSubmit} variant="contained" sx={{ bgcolor: '#003781' }}>
                        Approve & Submit
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Bandwidth Upgrade redirect confirmation ─────────────────── */}
            <Dialog open={bwRedirectOpen} onClose={() => setBwRedirectOpen(false)}>
                <DialogTitle>Leave this request?</DialogTitle>
                <DialogContent>
                    <Typography>
                        You will be taken to the Bandwidth Upgrade/Downgrade form. Anything you have entered here that is not saved will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBwRedirectOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => window.location.assign('/new-request?type=bandwidth_change')}>Proceed</Button>
                </DialogActions>
            </Dialog>

            {/* ── Success dialog ──────────────────────────────────────────── */}
            <Dialog open={successOpen} onClose={() => navigate('/track-requests')}>
                <DialogTitle>Request Submitted</DialogTitle>
                <DialogContent><Typography>Your site expansion request has been submitted successfully.</Typography></DialogContent>
                <DialogActions><Button onClick={() => navigate('/track-requests')}>Go to Track Requests</Button></DialogActions>
            </Dialog>
        </>
    );
};

export default SiteExpansionForm;
